// Cart-drawer cross-sell rules (SSOT for suggestion handles).
// Triggers inspect MERCHANDISE lines only (warranty add-ons and free
// gifts never trigger suggestions). Max 3 suggestions; anything already
// in the cart is hidden.
import { API_BASE } from "./apiBase";
import { isWarrantyLine } from "./warrantyAddon";
import { isFreebieLine, lineHandle } from "./freebies";

const TYPE_ZERO_PAD = "talongames-type-zero-balanced-mousepad";
const GLASS_PAD_P1 = "talongames-ultrapad-p1-glass-mousepad-free-skates";
const GLASS_PAD_P2 = "talongames-ultrapad-p2-glass-mousepad-free-skates";
const JKLP_CABLE = "jklp-8000hz-magnetic-axis-aviator-coiled-cable-type-c";
const ZNEO_HUB = "hm-lab-z-neo-8k-usb-gaming-hub";

const MOUSE_TYPES = new Set(["mouse", "mice"]);

function merchLines(lines) {
        return (lines || []).filter((l) => !isWarrantyLine(l) && !isFreebieLine(l));
}
function typeOf(line) {
        return String(line?.merchandise?.product?.productType || "").trim().toLowerCase();
}
function linePrice(line) {
        return Number(line?.merchandise?.price?.amount || 0);
}

// Rules:
//   mouse < ₹5,000            → Talongames Type Zero pad
//   mouse ≥ ₹7,000            → Ultrapad P1 glass pad (P2 if P1 in cart)
//   any keyboard              → JKLP 8K coiled cable
//   mousepad + keyboard combo → HM Lab Z-NEO 8K hub
export function crossSellHandles(lines) {
        const merch = merchLines(lines);
        if (!merch.length) return [];
        const inCart = new Set(merch.map(lineHandle));
        const mice = merch.filter((l) => MOUSE_TYPES.has(typeOf(l)));
        const hasKeyboard = merch.some((l) => typeOf(l).includes("keyboard"));
        const hasPad = merch.some((l) => typeOf(l).includes("mousepad"));
        const out = [];
        const push = (h) => {
                if (h && !inCart.has(h) && !out.includes(h)) out.push(h);
        };

        if (hasPad && hasKeyboard) push(ZNEO_HUB);
        for (const m of mice) {
                const price = linePrice(m);
                if (price < 5000) push(TYPE_ZERO_PAD);
                else if (price >= 7000) push(inCart.has(GLASS_PAD_P1) ? GLASS_PAD_P2 : GLASS_PAD_P1);
        }
        if (hasKeyboard) push(JKLP_CABLE);
        return out.slice(0, 3);
}

// Product card data for a suggestion handle; null when unpublished or not
// for sale (suggestion silently drops out of the block).
const _cache = {};
export async function fetchSuggestion(handle) {
        if (_cache[handle] !== undefined) return _cache[handle];
        try {
                const r = await fetch(`${API_BASE}/api/shopify/product/${handle}`, { credentials: "include" });
                if (!r.ok) {
                        _cache[handle] = null;
                        return null;
                }
                const p = await r.json();
                const node = p?.variants?.edges?.[0]?.node || {};
                const variantId = p?.firstVariant?.id || node?.id || null;
                if (p?.availableForSale === false || !variantId) {
                        _cache[handle] = null;
                        return null;
                }
                _cache[handle] = {
                        handle,
                        title: p?.title || handle,
                        price: Number(node?.price?.amount ?? p?.priceRange?.minVariantPrice?.amount ?? 0),
                        image: p?.featuredImage?.url || node?.image?.url || null,
                        variantId,
                };
        } catch {
                return null; // don't cache network errors
        }
        return _cache[handle];
}
